import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import {
  catchError,
  concatMap,
  defer,
  EMPTY,
  forkJoin,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { ProfileSettingsService } from '../profile-settings.service';

interface TranslationCatalogIndex {
  readonly defaultLanguage: string;
  readonly languages: readonly string[];
}

function canonicalLocale(locale: string): string {
  if (typeof locale !== 'string' || !/^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(locale)) {
    throw new RangeError(`Invalid translation locale: ${locale}`);
  }
  return Intl.getCanonicalLocales(locale)[0];
}

function validateCatalogIndex(raw: unknown): TranslationCatalogIndex {
  const invalid = () => new TypeError('Invalid translation catalog index');
  if (
    raw === null ||
    typeof raw !== 'object' ||
    Array.isArray(raw) ||
    Object.keys(raw).length !== 2 ||
    !Object.hasOwn(raw, 'defaultLanguage') ||
    !Object.hasOwn(raw, 'languages')
  ) {
    throw invalid();
  }

  const index = raw as Record<string, unknown>;
  if (
    typeof index['defaultLanguage'] !== 'string' ||
    !Array.isArray(index['languages']) ||
    index['languages'].length === 0
  ) {
    throw invalid();
  }

  const canonicalIds = new Set<string>();
  const languages = index['languages'].map((id: unknown) => {
    if (typeof id !== 'string') throw invalid();
    let canonical: string;
    try {
      canonical = canonicalLocale(id);
    } catch {
      throw invalid();
    }
    if (canonicalIds.has(canonical)) throw invalid();
    canonicalIds.add(canonical);
    return id;
  });

  let preferred: string;
  try {
    preferred = canonicalLocale(index['defaultLanguage']);
  } catch {
    throw invalid();
  }
  const defaultLanguage =
    languages.find((id) => canonicalLocale(id) === preferred) ??
    languages.find((id) => canonicalLocale(id) === 'en') ??
    languages[0];
  if (!defaultLanguage) throw invalid();

  return Object.freeze({
    defaultLanguage,
    languages: Object.freeze(languages),
  });
}

@Injectable({ providedIn: 'root' })
export class TranslationLanguageService {
  private readonly profileSettings = inject(ProfileSettingsService);
  private readonly translate = inject(TranslateService);
  private readonly loader = inject(TranslateLoader);
  private readonly languages = signal<readonly string[]>(Object.freeze([]));
  readonly availableLanguages = this.languages.asReadonly();
  private indexRequest?: Observable<TranslationCatalogIndex>;
  private initialization?: Observable<void>;
  private readonly switches = new Subject<{ locale: string; ready: ReplaySubject<void> }>();

  constructor() {
    this.switches
      .pipe(
        concatMap(({ locale, ready }) =>
          this.activate(locale).pipe(
            tap(() => {
              ready.next();
              ready.complete();
            }),
            catchError((error: unknown) => {
              ready.error(error);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  /** Loads the generated index and activates the saved or default language. */
  initialize(): Observable<void> {
    return defer(() => {
      this.initialization ??= this.initialLocale().pipe(
        switchMap((locale) => this.enqueue(locale)),
        catchError((error: unknown) => {
          this.initialization = undefined;
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      return this.initialization;
    });
  }

  useLanguage(locale: string): Observable<void> {
    return this.resolveLocale(locale).pipe(
      switchMap((lang) =>
        this.initialize().pipe(
          switchMap(() => this.enqueue(lang)),
          tap(() => this.profileSettings.setLanguage(this.translate.currentLang)),
        ),
      ),
    );
  }

  private loadIndex(): Observable<TranslationCatalogIndex> {
    return defer(() => {
      this.indexRequest ??= this.loader.getTranslation('catalog-index').pipe(
        map(validateCatalogIndex),
        tap((index) => this.languages.set(index.languages)),
        catchError((error: unknown) => {
          this.indexRequest = undefined;
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      return this.indexRequest;
    });
  }

  private getDefaultLanguage(): Observable<string> {
    return this.loadIndex().pipe(
      map((index) => {
        const available = this.languages();
        const language = available.includes(index.defaultLanguage) ? index.defaultLanguage : available[0];
        if (!language) throw new Error('No translation catalogs are available');
        return language;
      }),
    );
  }

  private resolveLocale(locale: string): Observable<string> {
    return defer(() => {
      const canonical = canonicalLocale(locale);
      return this.loadIndex().pipe(
        map(() => {
          const language = this.languages().find((id) => canonicalLocale(id) === canonical);
          if (!language) throw new RangeError(`Unsupported translation locale: ${locale}`);
          return language;
        }),
      );
    });
  }

  private markUnavailable(locale: string): void {
    this.languages.update((languages) => Object.freeze(languages.filter((id) => id !== locale)));
  }

  private initialLocale(): Observable<string> {
    const storedLocale = this.profileSettings.getLanguage();
    if (storedLocale == null) return this.getDefaultLanguage();
    return this.resolveLocale(storedLocale).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof RangeError)) return throwError(() => error);
        this.profileSettings.removeLanguage();
        return this.getDefaultLanguage();
      }),
    );
  }

  private enqueue(locale: string): Observable<void> {
    return defer(() => {
      const ready = new ReplaySubject<void>(1);
      this.switches.next({ locale, ready });
      return ready.asObservable();
    });
  }

  private prepare(lang: string): Observable<void> {
    if (this.translate.translations[lang]) return of(undefined);
    return this.loader.getTranslation(lang).pipe(
      tap((translations) => this.translate.setTranslation(lang, translations)),
      map(() => undefined),
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
          return throwError(() => error);
        }
        this.markUnavailable(lang);
        if (this.profileSettings.getLanguage() === lang) this.profileSettings.removeLanguage();
        return of(undefined);
      }),
    );
  }

  private activate(locale: string): Observable<void> {
    return this.getDefaultLanguage().pipe(
      switchMap((fallback) => {
        const selected = this.languages().includes(locale) ? locale : fallback;
        return forkJoin([...new Set([fallback, selected])].map((lang) => this.prepare(lang))).pipe(
          switchMap(() => {
            const available = this.languages();
            if (!available.includes(fallback) || !available.includes(selected)) {
              return this.activate(selected);
            }
            this.translate.setDefaultLang(fallback);
            return this.translate.use(selected).pipe(map(() => undefined));
          }),
        );
      }),
    );
  }
}
